import { createEffect, createMemo, createSignal, onCleanup, onMount, Show, untrack } from 'solid-js';
import { useNavigate, useSearchParams } from '@solidjs/router';
import Text from '../components/Text';
import CraftPlanView from '../components/CraftPlanView';
import ItemIdentifierPicker from '../components/ItemIdentifierPicker';
import QuantityInput from '../components/QuantityInput';
import BotSelector from '../components/BotSelector';
import StorageSelect from '../components/StorageSelect';
import { EmptyState } from '../components/EmptyState';
import { useToast } from '../components/MessageBox';
import { useTranslation } from '../hooks/useTranslation';
import { useItemName } from '../hooks/useItemName';
import { useItemStacks } from '../hooks/useItemStacks';
import { useCraftingResolver } from '../services/craftingResolverContext';
import { parseItemKey } from '../services/craftingResolver';
import { useInventoryApi, type CraftPlan, type CustomItemDefinition, type StorageInfo } from '../services/inventoryApi';
import styles from './InventoryCraft.module.css';

const PLAN_DEBOUNCE_MS = 350;

export default function InventoryCraft() {
    const { t } = useTranslation();
    const toast = useToast();
    const navigate = useNavigate();
    const inv = useInventoryApi();
    const resolver = useCraftingResolver();
    const { itemName } = useItemName();
    const [searchParams] = useSearchParams();

    const [storages, setStorages] = createSignal<StorageInfo[]>([]);
    const [loading, setLoading] = createSignal(true);

    const [itemKey, setItemKey] = createSignal('');
    const [quantity, setQuantity] = createSignal(1);
    const [sourceStorageId, setSourceStorageId] = createSignal('');
    const [resultStorageId, setResultStorageId] = createSignal('');
    const [botId, setBotId] = createSignal<string | null>(null);

    const [plan, setPlan] = createSignal<CraftPlan | null>(null);
    const [planLoading, setPlanLoading] = createSignal(false);
    const [submitting, setSubmitting] = createSignal(false);

    // The tenant's NBT item registry: the only place a hashed key's picture and name come from.
    const [customItems, setCustomItems] = createSignal<CustomItemDefinition[]>([]);
    const { customStackFor } = useItemStacks(customItems);

    const identifierFor = (key: string) => {
        const def = resolver()?.getAllItems()?.find(i => i.key === key);
        return def ? `minecraft:${def.name}:${def.metadata}` : null;
    };

    const keyForIdentifier = (identifier: string) => {
        const bare = identifier.replace(/^minecraft:/, '');
        const [name, metaText] = bare.split(':');
        const items = resolver()?.getAllItems() ?? [];
        const def = metaText !== undefined
            ? items.find(i => i.name === name && i.metadata === Number(metaText))
            : items.find(i => i.name === name);
        return def?.key ?? null;
    };

    const pickerValue = createMemo(() => {
        const key = itemKey();
        if (!key) return [];
        if (parseItemKey(key)?.hash) return [key];
        const identifier = identifierFor(key);
        return identifier ? [identifier] : [];
    });

    onMount(async () => {
        inv.fetchCustomItems().then(setCustomItems).catch(() => {});
        try {
            const list = await inv.fetchStorages();
            setStorages(list);

            const presetSource = (searchParams.source as string | undefined) ?? list[0]?.id ?? '';
            setSourceStorageId(presetSource);
            setResultStorageId(presetSource);

            const presetItem = searchParams.item as string | undefined;
            if (presetItem) setItemKey(presetItem);

            const presetQuantity = Number(searchParams.times);
            if (Number.isFinite(presetQuantity) && presetQuantity > 0) setQuantity(presetQuantity);
        } catch {
            toast?.error(t('INVENTORY_CRAFT_STORAGES_FAILED'));
        } finally {
            setLoading(false);
        }
    });

    const [eligibleBotIds, setEligibleBotIds] = createSignal<string[]>([]);

    createEffect(() => {
        const source = sourceStorageId();
        const result = resultStorageId();
        if (!source || !result) { setEligibleBotIds([]); return; }
        untrack(() => { void loadEligibleBots(source, result); });
    });

    const loadEligibleBots = async (source: string, result: string) => {
        try {
            const sourceBots = await inv.fetchStorageBots(source);
            if (source === result) {
                setEligibleBotIds(sourceBots.map(b => b.botRegistryId));
                return;
            }
            const resultBots = await inv.fetchStorageBots(result);
            const resultIds = new Set(resultBots.map(b => b.botRegistryId));
            setEligibleBotIds(sourceBots.map(b => b.botRegistryId).filter(id => resultIds.has(id)));
        } catch {
            setEligibleBotIds([]);
        }
    };

    let planTimer: number | undefined;
    createEffect(() => {
        const source = sourceStorageId();
        const result = resultStorageId();
        const key = itemKey();
        const count = quantity();
        const bot = botId();

        if (!source || !result || !key || count <= 0) { setPlan(null); return; }

        untrack(() => {
            window.clearTimeout(planTimer);
            planTimer = window.setTimeout(() => { void loadPlan(source, result, key, count, bot); }, PLAN_DEBOUNCE_MS);
        });
    });

    onCleanup(() => window.clearTimeout(planTimer));

    const loadPlan = async (source: string, result: string, key: string, count: number, bot: string | null) => {
        setPlanLoading(true);
        try {
            setPlan(await inv.planCraft({
                sourceStorageId: source,
                resultStorageId: result,
                itemKey: key,
                quantity: count,
                botId: bot,
            }));
        } catch {
            setPlan(null);
            toast?.error(t('INVENTORY_CRAFT_PLAN_FAILED'));
        } finally {
            setPlanLoading(false);
        }
    };

    const blockingError = createMemo(() => {
        const current = plan();
        if (!current) return null;
        if (current.planError) return current.planError;
        if (current.shortfalls.length > 0) return null;
        if (eligibleBotIds().length === 0) return t('INVENTORY_CRAFT_NO_SHARED_BOT');
        if (current.botError) return current.botError;
        return null;
    });

    const submitDisabled = () =>
        submitting() || planLoading() || !itemKey() || quantity() <= 0 || !sourceStorageId() || !resultStorageId()
        || !plan() || (plan()?.shortfalls.length ?? 0) > 0 || blockingError() !== null;

    const submit = async () => {
        setSubmitting(true);
        try {
            await inv.createCraftRequest({
                sourceStorageId: sourceStorageId(),
                resultStorageId: resultStorageId(),
                itemKey: itemKey(),
                quantity: quantity(),
                // A hashed key is named by the backend's registry lookup; the vanilla catalog would call it
                // by its base item.
                itemDisplayName: parseItemKey(itemKey())?.hash ? undefined : itemName(itemKey()),
                botId: botId(),
            });
            toast?.success(t('INVENTORY_CRAFT_QUEUED'));
            navigate('/inventory');
        } catch (err) {
            toast?.error(err instanceof Error ? err.message : t('INVENTORY_CRAFT_SUBMIT_FAILED'));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div class={styles.container}>
            <div class={styles.headerRow}>
                <h1><Text val="INVENTORY_CRAFT_TITLE" /></h1>
            </div>
            <p class={styles.subtitle}><Text val="INVENTORY_CRAFT_SUBTITLE" /></p>

            <Show when={!loading()} fallback={<div class={styles.skeletonCard} />}>
                <Show
                    when={storages().length > 0}
                    fallback={
                        <EmptyState
                            illustration="generic"
                            title={t('INVENTORY_CRAFT_NO_STORAGE_TITLE')}
                            hint={t('INVENTORY_CRAFT_NO_STORAGE_HINT')}
                        />
                    }
                >
                    <form class="fcard v" style={{ gap: 'var(--spacing-lg)' }} onSubmit={(e) => { e.preventDefault(); void submit(); }}>
                        <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                            <span class={styles.fieldLabel}><Text val="INVENTORY_CRAFT_ITEM" /></span>
                            <ItemIdentifierPicker
                                single
                                metaAware
                                value={pickerValue()}
                                stackForValue={customStackFor}
                                onChange={(next) => setItemKey(next[0] ? keyForIdentifier(next[0]) ?? '' : '')}
                                filter={(def) => resolver()?.hasRecipeForKey(def.key) ?? false}
                                placeholder={t('INVENTORY_CRAFT_ITEM_PLACEHOLDER')}
                                label={t('INVENTORY_CRAFT_ITEM')}
                            />
                        </label>

                        <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                            <span class={styles.fieldLabel}><Text val="INVENTORY_CRAFT_TIMES" /></span>
                            <QuantityInput value={quantity()} onChange={setQuantity} min={1} stackSize={resolver()?.getItem(itemKey())?.stackSize} />
                        </label>

                        <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                            <span class={styles.fieldLabel}><Text val="INVENTORY_CRAFT_SOURCE" /></span>
                            <StorageSelect
                                storages={storages()}
                                value={sourceStorageId()}
                                onChange={(id) => {
                                    const wasMirrored = resultStorageId() === sourceStorageId();
                                    setSourceStorageId(id);
                                    if (wasMirrored) setResultStorageId(id);
                                }}
                            />
                        </label>

                        <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                            <span class={styles.fieldLabel}><Text val="INVENTORY_CRAFT_RESULT" /></span>
                            <StorageSelect storages={storages()} value={resultStorageId()} onChange={setResultStorageId} />
                        </label>

                        <div class="v" style={{ gap: 'var(--spacing-xs)' }}>
                            <span class={styles.fieldLabel}><Text val="INVENTORY_CRAFT_BOT" /></span>
                            <Show
                                when={eligibleBotIds().length > 0}
                                fallback={<p class={styles.blocking}><i class="fa-solid fa-robot" /> <Text val="INVENTORY_CRAFT_NO_SHARED_BOT" /></p>}
                            >
                                <BotSelector
                                    storageKey="inventory_craft_bot"
                                    allowedIds={eligibleBotIds()}
                                    forceBotId={plan()?.resolvedBotId ?? undefined}
                                    onSelect={(id) => setBotId(id)}
                                />
                            </Show>
                        </div>

                        <div class="h" style={{ 'justify-content': 'flex-end', gap: 'var(--spacing-sm)' }}>
                            <button type="button" class="btn btn-secondary" onClick={() => navigate('/inventory')}>
                                <Text val="CANCEL" />
                            </button>
                            <button type="submit" class="btn btn-primary" disabled={submitDisabled()}>
                                <Show when={submitting()} fallback={<><i class="fa-solid fa-hammer" /> <Text val="INVENTORY_CRAFT_SUBMIT" /></>}>
                                    <i class="fa-solid fa-spinner fa-spin" />
                                </Show>
                            </button>
                        </div>
                    </form>

                    <div class="fcard v" style={{ gap: 'var(--spacing-md)' }}>
                        <h2 style={{ margin: 0 }}><Text val="INVENTORY_CRAFT_PLAN_TITLE" /></h2>

                        <Show
                            when={!planLoading()}
                            fallback={<div class={styles.centerWait}><i class="fa-solid fa-spinner fa-spin fa-2x" /></div>}
                        >
                            <Show
                                when={plan()}
                                fallback={<p class={styles.subtitle}><Text val="INVENTORY_CRAFT_PLAN_HINT" /></p>}
                            >
                                {(current) => (
                                    <>
                                        <CraftPlanView
                                            steps={current().steps}
                                            shortfalls={current().shortfalls}
                                            totalConsumed={current().totalConsumed}
                                            estimatedSeconds={current().estimatedSeconds}
                                            planError={current().planError}
                                            customItems={customItems()}
                                        />

                                        <Show when={blockingError()}>
                                            <p class={styles.blocking}><i class="fa-solid fa-triangle-exclamation" /> {blockingError()}</p>
                                        </Show>
                                    </>
                                )}
                            </Show>
                        </Show>
                    </div>
                </Show>
            </Show>
        </div>
    );
}
